/// <reference types="adal/adal-angular" />

import { ServiceDeskConfig, LegacyAuthenticationStorageKey } from "./serviceDeskConfig";
import { RouteLoadingData, PrerequisiteLoadingService } from "../sdServices/loading/prerequisite-loading-service";
import { LoggingService, UserVisibleMessage } from "../sdServices/loggingSvc";
import { AuthorizationService } from "../sdServices/authorization/authorization";
import { AgentProfileEntityServiceImplementation } from "../sdServices/entity-service-implementation/agent-profile-entity-service";
import { LocalizationService } from "../../ShellCommon/localization/localization";
import { onMsalCallback } from "../sdServices/authentication/authentication-tools";
import { RouteResolveWrapper } from "../sdServices/loading/route-resolve-wrapper";
import { FrameworkFlightingService, FlightingService } from "../sdServices/flightingSvc";
import { JarvisService } from "../sdServices/jarvisSvc";
import { EntryStateService } from "../sdServices/authentication/entry-state-service";
import { nonStandardPushInformationEvent } from "../sdServices/non-standard-logging";
import { ReleaseNotesService } from "../sdServices/release-notes-config/release-notes-service";
import { ResourceTimingLoggerService } from "../../ShellCommon/logger/resource-timing-logger";
import { v4 } from "uuid";
import { MsalAuthenticationServiceImplementation } from "../sdServices/msal/msal-authentication-service";

export let locationChangeHandler = ($location: ng.ILocationService) => {
  // Handle useLegacyAuth angular-level query strings. This allows developers to postfix the query string completely and for it to update the setting for the next page refresh.
  let searchObject = $location.search();
  if (searchObject.useLegacyAuth === "false") {
    sessionStorage.setItem(LegacyAuthenticationStorageKey, "false");
  } else if (searchObject.useLegacyAuth === "true") {
    sessionStorage.setItem(LegacyAuthenticationStorageKey, "true");
  }
};

let routeLoadingData = new Map<string, RouteLoadingData>();
let routeServiceParams: any;

export class SdAppRunOptions {
  // @ngInject
  constructor(
    $state: ng.ui.IStateService,
    $rootScope: ng.IRootScopeService,
    $location: ng.ILocationService,
    loggingService: LoggingService,
    msalAuthenticationService: MsalAuthenticationServiceImplementation,
    prerequisiteLoadingService: PrerequisiteLoadingService,
    authorizationService: AuthorizationService,
    agentProfileEntityService: AgentProfileEntityServiceImplementation,
    sdLocalizationService: LocalizationService,
    releaseNotesService: ReleaseNotesService,
    flightingService: FlightingService,
    jarvisService: JarvisService,
    $window: ng.IWindowService,
    resourceTimingLoggerService: ResourceTimingLoggerService
  ) {
    let skipReleaseNotes = false;
    const SKIP_RELEASE_NOTES_QUERY_PARAM = "skipReleaseNotes";
    if ($window.location.href.includes(SKIP_RELEASE_NOTES_QUERY_PARAM + "=true")) {
      skipReleaseNotes = true;
    }

    // assign a unique id to the window, if it's not already assigned
    if (!$window.name || $window.name.length == 0) {
      $window.name = v4();
    }
    let goingToErrorPage = false;
    // Support the 'redirectTo' parameter, allowing an emulation of abstract state redirection.
    $rootScope.$on("$stateChangeStart", function (evt, to, params) {
      if (goingToErrorPage) {
        loggingService.logError(`state change to ${to.name} cancelled: error occurred, navigating to error page.`);
        evt.preventDefault();
        return;
      }
      if (to.name == "sdSystem.error") {
        goingToErrorPage = true;
      }
      if (msalAuthenticationService.loginInProgress()) {
        loggingService.logInfo(`${evt.name} Suppressed a state change to ${to.name} because login was in progress`);
        evt.preventDefault();
      } else if (to.redirectTo) {
        loggingService.logInfo(`${evt.name} Suppressed a state change to ${to.name} because it is a redirect`);
        evt.preventDefault();
        $state.go(to.redirectTo, params);
      } else {
        loggingService.logInfo(`${evt.name} starting to ${to.name}`);
      }
    });

    $rootScope.$on("$stateChangeSuccess", (evt, to) => {
      if (
        to.requireADLogin &&
        (!authorizationService.hasAnyRight() || !agentProfileEntityService.getAgentProfileSync())
      ) {
        if ($state.current.name == "sdSystem.accessrequest") {
          $state.go("sdSystem.accessrequest");
        } else if ($state.current.name != "sdSystem.unauthorized") {
          $state.go("sdSystem.unauthorized");
        }
        let message = sdLocalizationService.getLocalizedString("ServiceDesk", "_USER_WITHOUT_ROLESET_WARNING_");
        loggingService.logWarning(message, <UserVisibleMessage>{
          displayText: message,
          uId: "sdBrowserWarning"
        });
      }
      goingToErrorPage = false;

      const RELEASE_NOTES_FLIGHT_ID = "SD_RELEASE_NOTES";
      if (
        flightingService.isFlightEnabled(RELEASE_NOTES_FLIGHT_ID) &&
        !skipReleaseNotes &&
        $state.current.name.toLocaleLowerCase().includes("home")
      ) {
        releaseNotesService.initReleaseNotes();
      }
      resourceTimingLoggerService.Initialize();
    });

    $rootScope.$on("$stateChangeCancel", function (evt, to) {
      loggingService.logInfo(`${evt.name} to state ${to.name}`);
    });

    $rootScope.$on("$locationChangeStart", () => {
      return locationChangeHandler($location);
    });

    prerequisiteLoadingService.initializeRoutes(routeLoadingData, routeServiceParams.modules);
  }
}

export class SdAppConfiguration {
  // @ngInject
  constructor(
    $httpProvider: ng.IHttpProvider,
    $urlRouterProvider: angular.ui.IUrlRouterProvider,
    $stateProvider: angular.ui.IStateProvider,
    $ocLazyLoadProvider: oc.ILazyLoadProvider,
    sdRouteServiceProvider: any,
    $injector: ng.auto.IInjectorService,
    sdConfig: ServiceDeskConfig
  ) {
    let isInFrame = false;
    try {
      isInFrame = window != window.top;
    } catch (e) {
      console.error(e, "Frame Check Failed check for inconsistency");
    }

    let msalConfig = sdConfig.getMsalConfig();
    if (!isInFrame) {
      sdConfig.redirectUri = location.href.replace(location.hash, "");
      if (onMsalCallback()) {
        msalConfig.postLogoutRedirectUri = localStorage["lastPostLogoutRedirectUri"];
      } else {
        msalConfig.postLogoutRedirectUri += btoa(window.location.hash);
        localStorage["lastPostLogoutRedirectUri"] = msalConfig.postLogoutRedirectUri;
      }
    }

    // decorator to the stateprovider to dynamically attach the custom resolver to all the states
    // reference: https://github.com/angular-ui/ui-router/wiki/quick-reference#spdecoratorname--decorator
    $stateProvider.decorator("data", function (config: any) {
      config.resolve = angular.extend({}, config.resolve, {
        flightResolver: [
          "flightingService",
          "$state",
          (flightingService: FrameworkFlightingService, $state: any) => {
            if (config.flight) {
              flightingService
                .isFlightActive(config.flight)
                .then(function (data: boolean) {
                  if (data) {
                    return;
                  } else {
                    $state.go("sdSystem.unauthorized");
                  }
                })
                .catch(() => {
                  $state.go("home");
                });
            }
          }
        ]
      });
    });

    $stateProvider.decorator("views", function (state: any, parent: any) {
      let result: any = {},
        views = parent(state);
      angular.forEach(views, function (config, name) {
        if (config.flight) {
          // here inject the resolve (if not existing)
          config.resolve = config.resolve || {};
          // and extend it with the auth stuff above
          config.resolve.isUserInFlight = [
            "flightingService",
            (flightingService: FrameworkFlightingService) => {
              return flightingService
                .isFlightActive(config.flight)
                .then(function (data: boolean) {
                  return data;
                })
                .catch((error: any) => {
                  return false;
                });
            }
          ];
        }
        result[name] = config;
      });
      return result;
    });

    let params = sdRouteServiceProvider.init();
    routeServiceParams = params;
    let pushLog = (message: string) => {
      try {
        nonStandardPushInformationEvent("app-startup", message);
      } catch (error) {
        console.error(error, "not able to log to asimov during service desk load");
      }
      console.log(message);
    };

    if (!isInFrame) {
      pushLog("Normal scenario, not in iframe and loading all modules");
      if (params.modules.length > 0) {
        $ocLazyLoadProvider.config({
          modules: params.modules.filter((module: any) => {
            return module.preventLazyLoad != true;
          }),
          debug: true
        });
      }
      let resolveWrapper = new RouteResolveWrapper($injector);
      for (let i = 0; i < params.serviceDeskrouter.length; i++) {
        let stateData = params.serviceDeskrouter[i];
        if (stateData && stateData.name) {
          if (stateData.data.requireADLogin == undefined) {
            stateData.data.requireADLogin = true;
          }
          let routeData: RouteLoadingData = {
            deferral: undefined,
            originalResolve: stateData.data.resolve,
            stateData: stateData
          };
          resolveWrapper.wrapResolve(stateData, routeData);

          routeLoadingData.set(stateData.name, routeData);
          $stateProvider.state(stateData.name, stateData.data);
        }
      }

      $urlRouterProvider.otherwise(($injector: ng.auto.IInjectorService, $location: ng.ILocationService) => {
        let path = $location.path();
        let $state = $injector.get<ng.ui.IStateService>("$state");
        let loggingService = $injector.get<LoggingService>("loggingService");
        let entryStateService = $injector.get<EntryStateService>("entryStateService");
        let msalAuthenticationService: MsalAuthenticationServiceImplementation =
          $injector.get<MsalAuthenticationServiceImplementation>("msalAuthenticationService");

        if (path === "" || path === "/") {
          // Handle root paths
          entryStateService.goToEntryState();
        } else if ($location.path().startsWith("/id_token=")) {
          // While msal is still processing the id_token, do not navigate anywhere
        } else if ($location.path().startsWith("/code=")) {
          return msalAuthenticationService.handleRedirectPromise().then(() => (location.href = sdConfig.redirectUri));
        } else {
          // Normal 404
          loggingService.logWarning(
            "An invalid route was navigated to. A 404 was shown for the user for path: " + path
          );
          $state.go("sdSystem.notfound");
        }
      });
    } else {
      pushLog("Going into refresh token scenario, in Iframe and  skipping all modules");
      $urlRouterProvider.otherwise("refreshview");
      $stateProvider.state("refreshview", {
        url: "/refreshview",
        views: {
          globalHeader: {
            template: "<h1>refreshView Load Nothing</h1>"
          },
          content: {
            template: "<h2>refreshView Load Nothing</h2>"
          }
        }
      });
    }
  }
}
